import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Check } from "lucide-react";
import { fetchProduct, fetchProducts, fetchCategories, imgUrl } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useQuote } from "@/context/QuoteContext";
import { formatPrice } from "@/components/site/specIcons";
import { Skeleton } from "@/components/ui/skeleton";

function ZoomImage({ src, alt, label }) {
  const ref = useRef(null);
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
  };
  return (
    <div
      ref={ref}
      data-testid="zoom-image"
      onMouseEnter={() => setZoom(true)}
      onMouseLeave={() => setZoom(false)}
      onMouseMove={onMove}
      className="group relative h-fit aspect-[4/3] w-full cursor-zoom-in overflow-hidden bg-secondary md:self-start"
    >
      <img src={src} alt={alt} className="h-full w-full object-cover transition-transform duration-200 ease-out"
        style={{ transform: zoom ? "scale(2.2)" : "scale(1)", transformOrigin: `${pos.x}% ${pos.y}%` }} />
      {!zoom && (
        <span className="absolute bottom-4 start-4 bg-background/80 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground backdrop-blur">
          {label}
        </span>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { openQuote } = useQuote();
  const [active, setActive] = useState(0);
  const [activeColorName, setActiveColorName] = useState(null);

  const { data: product, isLoading } = useQuery({ queryKey: ["product", id], queryFn: () => fetchProduct(id) });
  const { data: all = [] } = useQuery({ queryKey: ["products", "all"], queryFn: () => fetchProducts("all") });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });

  useEffect(() => { setActiveColorName(null); }, [id]);
  useEffect(() => { setActive(0); }, [activeColorName, id]);

  if (isLoading || !product) {
    return (
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 pt-32 md:grid-cols-2 md:px-10">
        <Skeleton className="aspect-[4/3] w-full" />
        <div className="space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-24 w-full" /><Skeleton className="h-12 w-1/2" /></div>
      </div>
    );
  }

  const catName = categories.find((c) => c.slug === product.category)?.name || product.category;
  const related = all.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);
  const selectedColor = product.colors?.find((c) => c.name === activeColorName);
  const images = selectedColor?.images?.length
    ? selectedColor.images
    : (product.images?.length ? product.images : [""]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-24 pt-28 md:px-10 md:pt-32">
      <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <Link to="/" className="transition-colors hover:text-primary">{t("detail.back")}</Link>
        <ChevronRight className="h-3.5 w-3.5 rtl:rotate-180" />
        <span>{catName}</span>
      </nav>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <ZoomImage src={imgUrl(images[active])} alt={product.name} label={t("detail.zoom")} />
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((im, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActive(i)}
                  className={`h-20 w-16 overflow-hidden border-2 transition-colors ${active === i ? "border-primary" : "border-transparent"}`}>
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="flex flex-col">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.3em] text-primary">{catName}</span>
            {product.sku && <span className="text-xs text-muted-foreground">· {product.sku}</span>}
            {product.featured && <span className="bg-primary px-2 py-0.5 text-[10px] font-bold uppercase text-primary-foreground">{t("products.featured")}</span>}
          </div>
          <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-none tracking-tight md:text-5xl">{product.name}</h1>

          <div className="mt-6 flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">{t("products.from")}</span>
            <span className="font-display text-3xl font-bold">{formatPrice(product.price)}</span>
          </div>

          {/* REVİZE: Metin ayrıştırma (parsing) mantığı eklendi */}
          {product.description && (() => {
            const parts = product.description.split('*');
            const mainText = parts[0].trim();
            const listItems = parts.slice(1).map(p => p.trim()).filter(Boolean);
            
            return (
              <div className="mt-8">
                <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t("detail.desc")}</p>
                <div className="leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {mainText && <p>{mainText}</p>}
                  {listItems.length > 0 && (
                    <ul className="mt-4 space-y-1.5 pl-5 list-disc marker:text-zinc-400">
                      {listItems.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })()}

          {product.fabric && (
            <p className="mt-6 text-sm"><span className="text-muted-foreground">{t("detail.fabric")}: </span><span className="font-medium">{product.fabric}</span></p>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t("detail.sizes")}</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <span key={s} className="min-w-10 border border-border px-3 py-1.5 text-center text-sm font-semibold">{s}</span>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t("detail.colors")}</p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => {
                  const isActive = activeColorName === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      data-testid={`color-swatch-${c.name}`}
                      onClick={() => setActiveColorName(isActive ? null : c.name)}
                      className={`flex items-center gap-2 text-sm transition-colors ${isActive ? "font-semibold text-primary" : ""}`}
                    >
                      <span
                        className={`h-5 w-5 rounded-full border border-border ${isActive ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
                        style={{ background: c.hex }}
                      />
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.features?.length > 0 && (
            <div className="mt-8">
              <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t("detail.specs")}</p>
              <div className="grid grid-cols-1 gap-px border border-border sm:grid-cols-2">
                {product.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 bg-secondary/40 p-3 text-sm font-medium">
                    <Check className="h-4 w-4 shrink-0 text-primary" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            data-testid="detail-quote-btn"
            onClick={() => openQuote(product)}
            className="mt-9 flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-[transform,box-shadow] duration-300 hover:scale-[1.02] hover:shadow-xl"
          >
            {t("products.getQuote")} <ArrowUpRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>

      {related.length > 0 && (
        <div className="mt-28">
          <h2 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">{t("detail.related")}</h2>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {related.map((p) => (
              <button key={p.id} data-testid={`related-${p.id}`}
                onClick={() => { navigate(`/urun/${p.id}`); setActive(0); window.scrollTo({ top: 0 }); }}
                className="group text-start">
                <div className="aspect-[4/3] overflow-hidden bg-secondary">
                  <img src={imgUrl(p.images?.[0])} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="mt-3 font-display font-semibold leading-tight transition-colors group-hover:text-primary">{p.name}</h3>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}